import { useCallback, useEffect, useState } from "react";
import type { CreditPricing, CreditState, Invoice } from "api";
import { billingApi, creditsApi } from "api";
import { isForbidden, isUnauthenticated } from "utils/error.utils";
import type { LoadStatus } from "./useProfile";

export type UseCredits = {
  /** Balance, last top-up and auto top-up. Null until it arrives. */
  state: CreditState | null;
  /** Tiers, packs and cost per action; the calculator cannot price without it. */
  pricing: CreditPricing | null;
  /** Null while unknown; an empty list once Stripe has none. */
  invoices: Invoice[] | null;
  /** Follows `state` and `pricing`, the two reads the pane cannot do without. */
  status: LoadStatus;
  /** Ask again in the background: after Checkout returns or an auto top-up change. */
  reload: () => void;
};

/**
 * The company's QCredits, for Plan & Billing and the cabinet header. Invoices
 * are optional: a Stripe hiccup leaves the rest of the pane readable.
 */
export const useCredits = (onSessionLost: () => void): UseCredits => {
  const [state, setState] = useState<CreditState | null>(null);
  const [pricing, setPricing] = useState<CreditPricing | null>(null);
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [status, setStatus] = useState<LoadStatus>("loading");

  const load = useCallback(
    (background: boolean) => {
      let cancelled = false;

      Promise.all([creditsApi.show(), creditsApi.pricing()])
        .then(([shown, priced]) => {
          if (cancelled) return;
          setState(shown.data);
          setPricing(priced.data.data);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (isUnauthenticated(error)) {
            onSessionLost();
            setStatus("unauthorized");
            return;
          }
          if (background) return;

          setStatus(isForbidden(error) ? "unverified" : "failed");
        });

      billingApi
        .invoices()
        .then(({ data }) => {
          if (!cancelled) setInvoices(data.data);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (isUnauthenticated(error)) {
            onSessionLost();
            return;
          }
          if (!background) setInvoices(null);
        });

      return () => {
        cancelled = true;
      };
    },
    [onSessionLost]
  );

  useEffect(() => load(false), [load]);

  const reload = useCallback(() => {
    load(true);
  }, [load]);

  return { state, pricing, invoices, status, reload };
};
